import React, { useState, useEffect, useContext } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import styled from "styled-components";
import Column from "./KanbanColumn";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import useSidebarStore from "../../zustand-stores/SidebarStore";
import { save, retrieve } from "../../helpers/StorageHelper";
import {
  useChangeTaskLevel,
  useMarkSubtaskAsCompleted,
} from "../../Controllers/TaskManagementController";
import { useQueryClient } from "@tanstack/react-query";
import {
  transformApiData,
  handleMoveTask as handleMoveTaskHelper,
  buildTaskLevelUpdateRequestBody,
  validateTaskMove,
  findDraggedItem,
  reorderItemsInColumn,
  updateColumnsForDragDrop,
  extractActualTaskId,
  getLevelFromColumnId,
} from "../../helpers/TaskManagement/kanbanHelper";
import { formatDate as formatDateHelper } from "../../helpers/DateHelper";

const KanbanBoard = ({ selectedDate, onTaskUpdate, render }) => {
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletedType, setShowCompletedType] = useState("done"); // 'done' or 'achieved'

  const { sidebarCollapsed } = useSidebarStore();
  const { mutate: changeTaskLevel, isPending: isChangingTaskLevel } =
    useChangeTaskLevel();
  const { mutate: markSubtaskAsCompleted, isPending: isMarkingSubtask } =
    useMarkSubtaskAsCompleted();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  const queryClient = useQueryClient();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchTasks = async () => {
    try {
      if (!selectedDate) {
        selectedDate = new Date();
        return;
      }
      const formattedDate = formatDateHelper(selectedDate);

      const response = await api.get(`/tasks?date=${formattedDate}`);

      if (response.data.status && response.data.data.length > 0) {
        const apiData = response.data.data[0];

        // Debug MIT List specifically
        if (apiData["Mit List"]) {
        }

        // Debug Achieved tasks
        if (apiData["Achieved"]) {
          // Filter achieved tasks for selected date
          const achievedForDate =
            apiData["Achieved"].tasks?.filter((task) => {
              if (!task.due_date || !selectedDate) return false;
              const taskDueDate = new Date(task.due_date);
              const selectedDateObj = new Date(selectedDate);
              return (
                taskDueDate.getFullYear() === selectedDateObj.getFullYear() &&
                taskDueDate.getMonth() === selectedDateObj.getMonth() &&
                taskDueDate.getDate() === selectedDateObj.getDate()
              );
            }) || [];
        }

        const transformedData = transformApiData(apiData, selectedDate);

        // Debug transformed MIT List

        setColumns(transformedData);

        // Also save to localStorage with date key
        save(`kanban-state-${formattedDate}`, transformedData);
      }
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch tasks:", err);
      toast.error("Failed to fetch tasks");

      // Fallback to localStorage if available
      try {
        const saved = retrieve("kanban-state");
        if (saved) {
          setColumns(saved);
        }
      } catch (e) {
        console.error("Failed to parse saved state", e);
      }
    } finally {
      setLoading(false);
    }
  };

  //get tasks from API
  useEffect(() => {
    fetchTasks();
  }, [selectedDate, showCompletedType]); // Add showCompletedType to dependencies

  // Validation function for task movement (wrapper around helper)
  const isValidMove = (sourceColumnId, destinationColumnId) => {
    const validation = validateTaskMove(
      sourceColumnId,
      destinationColumnId,
      showCompletedType,
    );

    if (!validation.isValid && validation.errorMessage) {
      toast.error(validation.errorMessage);
    }

    return validation.isValid;
  };

  const updateTaskLevel = async (
    taskId,
    newLevel,
    sourceColumnId,
    destinationColumnId,
  ) => {
    return new Promise((resolve) => {
      const formattedDate = formatDateHelper(selectedDate);
      const requestBody = buildTaskLevelUpdateRequestBody(
        taskId,
        newLevel,
        sourceColumnId,
        destinationColumnId,
        formattedDate,
      );

      changeTaskLevel(requestBody, {
        onSuccess: (data) => {
          onTaskUpdate();
          // Invalidate goals query to refresh goals data
          queryClient.invalidateQueries({
            queryKey: ["goalsAndTargets", "goals"],
          });
          resolve({ success: true });
        },
        onError: (error) => {
          console.error("Error updating task level:", error);
          const message = error?.data?.message || "Failed to update task";
          resolve({
            success: false,
            message: message,
          });
        },
      });
    });
  };

  const handleMoveTask = async (taskId, destinationColumnId) => {
    // Use helper function to get all necessary data
    const moveResult = handleMoveTaskHelper(
      taskId,
      destinationColumnId,
      columns,
      showCompletedType,
    );

    if (!moveResult.success) {
      toast.error(moveResult.error);
      return;
    }

    const {
      sourceColumnId,
      actualTaskId,
      newLevel,
      updatedColumns,
      originalColumns,
    } = moveResult;

    // Validate the move
    if (!isValidMove(sourceColumnId, destinationColumnId)) {
      return;
    }

    // Optimistic UI update
    setColumns(updatedColumns);

    // Then make the API call
    try {
      const result = await updateTaskLevel(
        actualTaskId,
        newLevel,
        sourceColumnId,
        destinationColumnId,
      );

      if (!result.success) {
        // Show error message if the update failed
        toast.error(result.message);

        // Revert the UI change
        setColumns(originalColumns);
      } else {
        fetchTasks();
      }
    } catch (err) {
      console.error("Error handling task move:", err);
      toast.error("Failed to update task status");

      // Revert the UI change
      setColumns(originalColumns);
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Validate the move
    if (!isValidMove(source.droppableId, destination.droppableId)) {
      return;
    }

    const start = columns[source.droppableId];
    const finish = columns[destination.droppableId];

    // Additional validation
    if (!start || !finish) return;

    // Find the dragged item using helper
    const item = findDraggedItem(
      start,
      source.droppableId,
      draggableId,
      showCompletedType,
    );

    if (!item) return;

    // Store original columns for rollback
    const originalColumns = columns;

    // Optimistic UI update
    if (source.droppableId === destination.droppableId) {
      // Same column reordering
      const updatedColumns = reorderItemsInColumn(
        columns,
        source.droppableId,
        source.index,
        destination.index,
        showCompletedType,
      );
      setColumns(updatedColumns);
    } else {
      // Moving between columns
      const updatedColumns = updateColumnsForDragDrop(
        columns,
        source.droppableId,
        destination.droppableId,
        draggableId,
        item,
        showCompletedType,
      );
      setColumns(updatedColumns);

      // Make API call for column change
      try {
        // Extract the task ID using helper
        const actualTaskId = extractActualTaskId(
          draggableId,
          source.droppableId,
        );

        // Get level using helper
        const newLevel = getLevelFromColumnId(
          destination.droppableId,
          showCompletedType,
        );

        const result = await updateTaskLevel(
          actualTaskId,
          newLevel,
          source.droppableId,
          destination.droppableId,
        );

        if (!result.success) {
          // Show error message if the update failed
          toast.error(result.message);

          // Revert the UI change
          setColumns(originalColumns);
        } else {
          fetchTasks();
        }
      } catch (err) {
        console.error("Error updating task level after drag:", err);
        toast.error("Failed to update task status");

        // Revert the UI change
        setColumns(originalColumns);
      }
    }
  };

  const onUpdate = () => {
    fetchTasks();
  };

  useEffect(() => {
    fetchTasks();
  }, [render]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error loading tasks: {error}</div>;
  }

  const handleSubtaskComplete = (taskId, subtaskId) => {
    // First find the task in our columns
    let task = null;
    let columnId = null;

    for (const [colId, column] of Object.entries(columns)) {
      const foundTask = column.items.find((item) => item.id === taskId);
      if (foundTask) {
        task = foundTask;
        columnId = colId;
        break;
      }
    }

    if (!task || !task.sub_tasks) return;

    // Toggle the subtask status in local state immediately for better UX
    const updatedSubtasks = task.sub_tasks.map((subtask) =>
      subtask.id === subtaskId
        ? {
            ...subtask,
            status: subtask.status === "completed" ? "pending" : "completed",
          }
        : subtask,
    );

    // Update the task in our state
    setColumns((prevColumns) => {
      const newColumns = { ...prevColumns };
      newColumns[columnId] = {
        ...newColumns[columnId],
        items: newColumns[columnId].items.map((item) =>
          item.id === taskId ? { ...item, sub_tasks: updatedSubtasks } : item,
        ),
      };
      return newColumns;
    });

    // Extract the actual task ID using the helper to handle all prefix cases
    let extractedId;
    try {
      extractedId = extractActualTaskId(taskId, columnId);
    } catch (e) {
      console.warn(
        "Failed to extract task ID with helper, falling back to simple split",
        e,
      );
    }

    // Fallback if helper returns undefined or fails (e.g. for simple IDs not covered)
    if (!extractedId) {
      extractedId = taskId.includes("-") ? taskId.split("-").pop() : taskId;
    }

    const actualTaskId = parseInt(extractedId, 10);
    const actualSubTaskId = parseInt(subtaskId, 10);

    // Use React Query hook to make the API call
    markSubtaskAsCompleted(
      { taskId: actualTaskId, subtaskId: actualSubTaskId },
      {
        onSuccess: () => {
          toast.success("Subtask status updated");
          fetchTasks();
          // The hook will automatically invalidate the queries
        },
        onError: (error) => {
          console.error("Error updating subtask:", error);
          toast.error("Failed to update subtask status");
          // Revert the local state change on error
          setColumns((prevColumns) => {
            const newColumns = { ...prevColumns };
            newColumns[columnId] = {
              ...newColumns[columnId],
              items: newColumns[columnId].items.map((item) =>
                item.id === taskId
                  ? { ...item, sub_tasks: task.sub_tasks }
                  : item,
              ),
            };
            return newColumns;
          });
        },
      },
    );
  };

  const toggleCompletedView = (type) => {
    setShowCompletedType(type);
    // This will trigger the useEffect to reload data with the new view
  };

  const onSubtaskDelete = () => {
    fetchTasks();
  };

  const onSubtaskReorder = () => {
    fetchTasks();
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <KanbanContainer id="taskKanban">
        {Object.values(columns)
          .filter((column) => {
            if (!sidebarCollapsed) {
              if (!isMobile) {
                return column.id !== "doneList" && column.id !== "achievedList";
              } else {
                return column.id !== "completedList";
              }
            } else {
              return column.id !== "completedList";
            }
          })
          .map((column) => (
            <Column
              key={column.id}
              column={column}
              onUpdate={onUpdate}
              onMoveTask={handleMoveTask}
              onSubtaskComplete={(subtaskId) =>
                handleSubtaskComplete(
                  column.items.find((item) =>
                    item.sub_tasks?.some((st) => st.id === subtaskId),
                  )?.id,
                  subtaskId,
                )
              }
              showGBorder={["achievedList", "doneList"].includes(column.id)}
              isAchievecolumn={["achievedList"].includes(column.id)}
              showCompletedType={
                column.id === "completedList" ? showCompletedType : null
              }
              onToggleCompletedView={
                column.id === "completedList" ? toggleCompletedView : null
              }
              onSubtaskDelete={onSubtaskDelete}
              onSubtaskReorder={onSubtaskReorder}
            />
          ))}
      </KanbanContainer>
    </DragDropContext>
  );
};

const KanbanContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 16px;
  gap: 16px;
  align-items: flex-start;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  flex-grow: 1;

  > * {
    flex: 0 0 320px;
    scroll-snap-align: start;
  }
`;

export default KanbanBoard;
